import { AtgChip } from "@/components/AtgChip";
import { PlayerName } from "@/components/PlayerCardDialog";
import { ArchetypeBadge } from "@/components/ArchetypeBadge";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const POS_COLOR = {
  C: "text-cyan-300 bg-cyan-950/60",
  LW: "text-emerald-300 bg-emerald-950/60",
  RW: "text-amber-300 bg-amber-950/60",
  D: "text-fuchsia-300 bg-fuchsia-950/60",
};

const Unit = ({ u }) => {
  const players = u.players || [];
  const best = players[0];
  const alts = players.slice(1);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5" data-testid={`gcs-unit-${u.team}-${u.position}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 font-mono text-xs">
        <span className="font-bold text-slate-100">{u.team}</span>
        <span className={`rounded px-1 font-bold ${POS_COLOR[u.position] || "bg-slate-800 text-slate-300"}`}>{u.position}</span>
        <span className="text-[10px] text-slate-500">vs {u.goalie_name || u.opponent}</span>
        <span className="rounded bg-rose-950/60 px-1 text-[10px] font-bold text-rose-300">
          {u.goalie_ga_pos}G / {u.goalie_games}GM ({u.goalie_gpg}/GM)
        </span>
        {u.first_goal > 0 && <span className="text-[9px] text-orange-300">{u.first_goal} 1st</span>}
        {u.multi_goal > 0 && <span className="text-[9px] text-purple-300">{u.multi_goal} multi</span>}
      </div>
      {best ? (
        <>
          <div className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-lime-500">Best candidate</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 rounded-md border border-lime-800/40 bg-lime-950/20 px-1.5 py-1 font-mono text-[11px] leading-tight" data-testid={`gcs-best-${best.jersey}`}>
            <span className="font-bold text-cyan-400">#{best.jersey ?? "—"}</span>
            <PlayerName name={best.name} team={u.team} className="font-bold uppercase text-slate-100" />
            <span className="rounded bg-slate-800 px-1 text-[9px] text-slate-400">{best.slot}</span>
            <ArchetypeBadge name={best.archetype} testId={`cheat-arch-${best.jersey}`} />
            <AtgChip atg={best.atg} testId={`cheat-atg-${best.jersey}`} />
            {best.pp_unit && <span className="rounded bg-amber-950/60 px-1 text-[9px] text-amber-300">{best.pp_unit}</span>}
            {best.origin_goals > 0 && (
              <span className="text-[9px] font-bold text-lime-400">{best.origin_goals}G/{best.origin_games}GM line</span>
            )}
          </div>
          {alts.length > 0 && (
            <div className="mt-1 font-mono text-[9px] text-slate-500">
              alt: {alts.map((p, i) => <span key={`${p.jersey}-${i}`}>{i > 0 && " · "}#{p.jersey ?? "—"} <PlayerName name={p.name} team={u.team} /> ({p.slot})</span>)}
            </div>
          )}
        </>
      ) : (
        <span className="font-mono text-[10px] text-slate-600">lineup unavailable</span>
      )}
    </div>
  );
};

export default function GoalieCheatSheet({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/api/goalie-cheat-sheet`, { params: apiDate ? { date: apiDate } : {}, withCredentials: true, timeout: 240000 });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to build goalie cheat sheet"); }
    finally { setLoading(false); }
  }, [apiDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="gcs-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Building goalie-origin cheat sheet…</p>
    </div>
  );
  if (err) return <div className="py-20 text-center font-mono text-sm text-rose-400" data-testid="gcs-error">{err}</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="gcs-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-800 pb-4">
        <div>
          <h2 className="flex items-center gap-2 font-mono text-lg font-black uppercase tracking-wider text-rose-300">
            <ShieldAlert className="h-5 w-5" /> Goalie-Origin Cheat Sheet
          </h2>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            Team+position pairs ranked by opposing goalie goals allowed · same &amp; cross-game · {data.slate_size} games
          </p>
          <p className="mt-1 font-mono text-[10px] text-amber-300/80" data-testid="gcs-analytical-note">Analytical view · official picks come only from the Official NHL Pair Board (Pairing tab)</p>
        </div>
        <div className="rounded-lg border border-rose-500/30 bg-rose-950/30 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-rose-300">
          {data.pairs.length} pairings
        </div>
      </div>

      {data.pairs.length === 0 ? (
        <p className="py-16 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">
          No goalie-origin pairs (no projected goalie has conceded to these positions yet)
        </p>
      ) : (
        <div className="space-y-2" data-testid="gcs-list">
          {data.pairs.map((p, i) => (
            <div key={p.pair_id} className={`rounded-xl border border-slate-800 bg-slate-900/30 p-3 ${i === 0 ? "ring-1 ring-rose-500/30" : ""}`} data-testid={`gcs-pair-${p.pair_id}`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  #{i + 1} · {p.unit_a.team} {p.unit_a.position} + {p.unit_b.team} {p.unit_b.position}
                  <span className={`ml-2 rounded px-1 py-0.5 text-[9px] ${p.same_game ? "bg-cyan-950/60 text-cyan-300" : "bg-slate-800 text-slate-400"}`}>
                    {p.same_game ? "SAME GAME" : "CROSS GAME"}
                  </span>
                </span>
                <span className="rounded-lg border border-rose-500/30 bg-rose-950/30 px-2 py-1 font-mono text-[11px] font-bold text-rose-300">
                  {p.combined_gpg}/GM · {p.combined_goals}G allowed
                </span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <div className="flex-1"><Unit u={p.unit_a} /></div>
                <div className="flex items-center justify-center px-1 font-display text-xl font-bold text-slate-600">+</div>
                <div className="flex-1"><Unit u={p.unit_b} /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
